import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./ForgotPassword.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { environment } from "environment/environment";

export default function ForgotPassword() {

  const [step, setStep] = useState(1);
  const [timer, setTimer] = useState(0);

  const [form, setForm] = useState({
    email: "",
    otp: ["", "", "", "", "", ""], // ✅ 6-digit OTP
    password: "",
    confirmPassword: ""
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // ⏱ OTP resend timer
  useEffect(() => {
    if (timer <= 0) return;
    const interval = setInterval(() => setTimer(t => t - 1), 1000);
    return () => clearInterval(interval);
  }, [timer]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  // 🔢 OTP handlers
  const handleOtpChange = (e, index) => {
    const value = e.target.value.replace(/\D/, "");
    if (!value) return;

    const newOtp = [...form.otp];
    newOtp[index] = value[0];
    setForm({ ...form, otp: newOtp });

    if (index < 5) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      const newOtp = [...form.otp];
      newOtp[index] = "";
      setForm({ ...form, otp: newOtp });

      if (index > 0) {
        document.getElementById(`otp-${index - 1}`)?.focus();
      }
    }
  };

  const handleOtpPaste = (e) => {
    const paste = e.clipboardData.getData("text").slice(0, 6);
    if (!/^\d+$/.test(paste)) return;
    setForm({ ...form, otp: paste.split("") });
  };

  // ✅ STEP 1 validation
  const validateEmail = () => {
    const err = {};
    if (!form.email.trim()) err.email = "Email is required";
    else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(form.email))
      err.email = "Invalid email address";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  // ✅ STEP 2 validation
  const validateReset = () => {
    const err = {};

    if (form.otp.join("").length !== 6)
      err.otp = "Enter 6-digit OTP";

    if (!form.password)
      err.password = "Password required";
    else if (form.password.length < 6)
      err.password = "Minimum 6 characters";

    if (form.password !== form.confirmPassword)
      err.confirmPassword = "Passwords do not match";

    setErrors(err);
    return Object.keys(err).length === 0;
  };

  // 📩 Send OTP
  const sendVerificationCode = async () => {
    if (!validateEmail()) return;

    try {
      setLoading(true);
      await axios.post(
        `${environment.baseUrl}/user/forgot-password/send-otp`,
        { email: form.email }
      );
      toast.success("OTP sent to your email");
      setStep(2);
      setTimer(60);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  // 🔁 Resend OTP
  const resendOtp = async () => {
    if (timer > 0) return;
    await sendVerificationCode();
  };

  // 🔐 Reset password
  const resetPassword = async () => {
    if (!validateReset()) return;

    try {
      setLoading(true);
      await axios.post(
        `${environment.baseUrl}/user/forgot-password/reset`,
        {
          email: form.email,
          otp: form.otp.join(""), // ✅ join OTP
          newPassword: form.password
        }
      );
      toast.success("Password reset successful");
      setTimeout(() => window.location.href = "/login", 1500);
    } catch (error) {
      toast.error(error.response?.data?.message || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="forgot-password-container">
      <form className="forgot-password-form card shadow-sm">

        <h4 className="text-center mb-4">Forgot Password</h4>

        {/* 🔵 STEPPER */}
        <div className="stepper">
          <div className={step >= 1 ? "active" : ""}>Email</div>
          <div className={step >= 2 ? "active" : ""}>Verify</div>
          <div className={step >= 3 ? "active" : ""}>Reset</div>
        </div>

        {/* STEP 1 */}
        {step === 1 && (
          <div className="fade-slide">
            <label>Email</label>
            <input
              type="email"
              name="email"
              className={`form-control ${errors.email ? "is-invalid" : ""}`}
              value={form.email}
              onChange={handleChange}
              placeholder="Enter registered email"
            />
            {errors.email && <div className="invalid-feedback">{errors.email}</div>}

            <button
              className="btn btn-primary w-100 mt-3"
              onClick={sendVerificationCode}
              disabled={loading}
              type="button"
            >
              {loading ? "Sending..." : "Get Verification Code"}
            </button>
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div className="fade-slide">
            <label>Verification Code</label>

            <div className="otp-boxes" onPaste={handleOtpPaste}>
              {form.otp.map((digit, index) => (
                <input
                  key={index}
                  id={`otp-${index}`}
                  type="text"
                  maxLength="1"
                  className="otp-input"
                  value={digit}
                  onChange={(e) => handleOtpChange(e, index)}
                  onKeyDown={(e) => handleOtpKeyDown(e, index)}
                />
              ))}
            </div>

            {errors.otp && (
              <div className="invalid-feedback d-block">{errors.otp}</div>
            )}

            <div className="resend">
              {timer > 0 ? (
                <span>Resend OTP in {timer}s</span>
              ) : (
                <button type="button" onClick={resendOtp}>
                  Resend OTP
                </button>
              )}
            </div>

            <button
              className="btn btn-success w-100 mt-3"
              onClick={() => setStep(3)}
              type="button"
            >
              Continue
            </button>
          </div>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <div className="fade-slide">
            <label>New Password</label>
            <input
              type="password"
              name="password"
              className={`form-control ${errors.password ? "is-invalid" : ""}`}
              value={form.password}
              onChange={handleChange}
            />

            <label className="mt-2">Confirm Password</label>
            <input
              type="password"
              name="confirmPassword"
              className={`form-control ${errors.confirmPassword ? "is-invalid" : ""}`}
              value={form.confirmPassword}
              onChange={handleChange}
            />

            <button
              className="btn btn-success w-100 mt-3"
              onClick={resetPassword}
              disabled={loading}
              type="button"
            >
              {loading ? "Resetting..." : "Reset Password"}
            </button>
          </div>
        )}

        <p className="text-center mt-3">
          Back to <Link to="/login">Login</Link>
        </p>
      </form>

      <ToastContainer position="top-center" />
    </div>
  );
}
